import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Check, X, Loader2, ArrowLeft } from "lucide-react";

const COPY = {
  es: {
    checking: "Verificando pago…",
    successTitle: "Pago confirmado.",
    successCopy:
      "Tu aplicación ha sido recibida y tu compromiso confirmado. Revisaremos tu perfil en 24–48h.",
    failedTitle: "Pago no completado.",
    failedCopy: "El pago no se completó correctamente. Puedes intentarlo de nuevo desde el formulario.",
    timeoutTitle: "Estamos verificando…",
    timeoutCopy: "El estado tarda más de lo esperado. Revisa tu email — te confirmaremos por allí.",
    backHome: "VOLVER AL INICIO",
    retry: "REINTENTAR APLICACIÓN",
    refLabel: "Referencia",
    amountLabel: "Importe",
  },
  en: {
    checking: "Verifying payment…",
    successTitle: "Payment confirmed.",
    successCopy:
      "Your application has been received and your commitment confirmed. We'll review your profile in 24–48h.",
    failedTitle: "Payment not completed.",
    failedCopy: "The payment was not completed correctly. You can retry from the form.",
    timeoutTitle: "Verifying…",
    timeoutCopy: "Status is taking longer than expected. Check your email — we'll confirm there.",
    backHome: "BACK TO HOME",
    retry: "RETRY APPLICATION",
    refLabel: "Reference",
    amountLabel: "Amount",
  },
};

export default function ApplySuccess() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.es;
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "checking", data: null });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "failed", data: null });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ phase: "success", data });
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setState({ phase: "failed", data });
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= 6) {
          setState({ phase: "timeout", data });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (cancelled) return;
        attemptsRef.current += 1;
        if (attemptsRef.current >= 4) {
          setState({ phase: "failed", data: null, error: formatApiErrorDetail(e.response?.data?.detail) });
          return;
        }
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-mtk-bg text-white flex flex-col" data-testid="apply-success-page">
      <header className="px-6 md:px-12 py-6 flex items-center justify-between border-b border-mtk-border">
        <Link to="/" className="flex items-center gap-2 text-zinc-400 hover:text-mtk-gold transition-colors" data-testid="success-back-home">
          <ArrowLeft className="w-4 h-4" />
          <span className="font-mono-mtk text-[10px] tracking-[0.3em] uppercase">HOME</span>
        </Link>
        <div className="flex items-baseline gap-2">
          <span className="font-heading text-lg font-black tracking-[0.2em] text-white">MTK</span>
          <span className="font-mono-mtk text-[10px] tracking-[0.3em] text-mtk-gold uppercase">SYSTEM</span>
        </div>
        <div className="w-16" />
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-xl">
          {state.phase === "checking" && (
            <div className="border border-mtk-border bg-mtk-surface p-10 md:p-14 text-center" data-testid="payment-checking">
              <Loader2 className="w-10 h-10 text-mtk-gold mx-auto animate-spin" strokeWidth={1.5} />
              <p className="mt-6 font-mono-mtk text-[11px] tracking-[0.35em] uppercase text-mtk-gold">
                {c.checking}
              </p>
            </div>
          )}

          {state.phase === "success" && (
            <div className="border border-mtk-gold/40 bg-mtk-surface p-10 md:p-14 text-center grain-overlay relative" data-testid="payment-success">
              <div className="inline-flex items-center justify-center w-16 h-16 border border-mtk-gold/50 mb-6">
                <Check className="w-7 h-7 text-mtk-gold" strokeWidth={2} />
              </div>
              <div className="font-mono-mtk text-[10px] tracking-[0.35em] uppercase text-mtk-gold">
                MTK SYSTEM · STATUS
              </div>
              <h1 className="mt-3 font-heading text-3xl md:text-4xl font-black uppercase tracking-tight text-white">
                {c.successTitle}
              </h1>
              <p className="mt-5 text-zinc-300 max-w-md mx-auto">{c.successCopy}</p>

              {state.data && (
                <div className="mt-8 grid grid-cols-2 gap-px bg-mtk-border border border-mtk-border text-left">
                  <div className="bg-mtk-bg p-4">
                    <div className="font-mono-mtk text-[9px] tracking-[0.3em] uppercase text-zinc-500">
                      {c.amountLabel}
                    </div>
                    <div className="mt-2 font-heading text-xl font-black text-mtk-gold">
                      €{Number(state.data.amount_total || 0).toFixed(2)}
                    </div>
                  </div>
                  <div className="bg-mtk-bg p-4">
                    <div className="font-mono-mtk text-[9px] tracking-[0.3em] uppercase text-zinc-500">
                      {c.refLabel}
                    </div>
                    <div className="mt-2 font-mono-mtk text-[11px] text-zinc-300 truncate" title={sessionId}>
                      {sessionId?.slice(0, 18)}…
                    </div>
                  </div>
                </div>
              )}

              <Link to="/">
                <Button
                  data-testid="success-home-btn"
                  className="mt-10 rounded-none bg-mtk-gold text-mtk-bg hover:bg-mtk-gold-light font-bold uppercase tracking-[0.25em] text-xs h-12 px-8"
                >
                  {c.backHome}
                </Button>
              </Link>
            </div>
          )}

          {state.phase === "failed" && (
            <div className="border border-red-900/40 bg-mtk-surface p-10 md:p-14 text-center" data-testid="payment-failed">
              <div className="inline-flex items-center justify-center w-16 h-16 border border-red-900/50 mb-6">
                <X className="w-7 h-7 text-red-400" strokeWidth={2} />
              </div>
              <h1 className="font-heading text-3xl md:text-4xl font-black uppercase tracking-tight text-white">
                {c.failedTitle}
              </h1>
              <p className="mt-5 text-zinc-300 max-w-md mx-auto">{c.failedCopy}</p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <a href="/#apply">
                  <Button
                    data-testid="failed-retry-btn"
                    className="rounded-none bg-mtk-gold text-mtk-bg hover:bg-mtk-gold-light font-bold uppercase tracking-[0.25em] text-xs h-12 px-6"
                  >
                    {c.retry}
                  </Button>
                </a>
                <Link to="/">
                  <Button
                    variant="outline"
                    className="rounded-none border-mtk-border bg-transparent text-white hover:bg-white/5 font-bold uppercase tracking-[0.25em] text-xs h-12 px-6"
                  >
                    {c.backHome}
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {state.phase === "timeout" && (
            <div className="border border-mtk-border bg-mtk-surface p-10 md:p-14 text-center" data-testid="payment-timeout">
              <Loader2 className="w-10 h-10 text-mtk-gold mx-auto" strokeWidth={1.5} />
              <h1 className="mt-6 font-heading text-2xl md:text-3xl font-black uppercase tracking-tight text-white">
                {c.timeoutTitle}
              </h1>
              <p className="mt-4 text-zinc-300 max-w-md mx-auto">{c.timeoutCopy}</p>
              <Link to="/">
                <Button
                  className="mt-8 rounded-none bg-mtk-gold text-mtk-bg hover:bg-mtk-gold-light font-bold uppercase tracking-[0.25em] text-xs h-12 px-8"
                >
                  {c.backHome}
                </Button>
              </Link>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
